import { lazy, Suspense, useEffect } from "react";
import { LegalScreen, legalPagesPublic, legalScreen } from "./legal";
import { useAuthState } from "./auth/state";
import { Empty } from "./components/bits";
import { Logo } from "./brand/Logo";
import { PlayerOverlay, Shell, Toasts } from "./components/Shell";
import { director } from "./director";
import { brandRedirect, Link, match, navigate, useLocation } from "./router";
import { AuthRoot, useGate } from "./auth/AuthRoot"; // v2.1 (D)
import { AuthScreen } from "./auth/screens"; // v2.1 (D)
import { ChannelScreen } from "./screens/Channel";
import { CharacterScreen } from "./screens/Character";
import { CollectionScreen } from "./screens/Collection";
import { CreditsScreen } from "./screens/Credits";
import { HomeScreen } from "./screens/Home";
import { LibraryScreen } from "./screens/Library";
import { AdminCostsScreen } from "./screens/AdminCosts";
import { AdminServicesScreen } from "./screens/AdminServices";
import { AdminFeedbackScreen } from "./screens/AdminFeedback";
import { PersonSpendScreen } from "./screens/PersonSpend";
import { InvitesScreen } from "./screens/Invites";
import { PeopleScreen } from "./screens/People";
import { startIdleWatch } from "./idleController";
import { ProjectScreen } from "./screens/Project";
import { ReviewScreen } from "./screens/Review"; // v4 (T4)
import { SettingsScreen } from "./screens/Settings";
import { ModelsScreen } from "./screens/Models";
import { AdminReportsScreen } from "./screens/AdminReports";
import { AdminShowcaseScreen } from "./screens/AdminShowcase";
import { AdminSafetyScreen } from "./screens/AdminSafety"; // Wave 1 safety
import { ShareHost } from "./components/ShareSheet";
import { sharePath } from "./share";
import { NewVideoHost } from "./components/NewVideo";
import { TourHost } from "./components/Tour"; // Wave 3b: the guided tour
import { UploadHost } from "./components/UploadHost";
import { PacksHost } from "./components/PacksSheet"; // Wave 3 billing
import { openPacksForError } from "./packs";
import { ReportButton } from "./components/ReportButton";
import { StageLayer } from "./stage/StageView";
import { startEvents, stopEvents } from "./session";
import { set, useRefresh, useStore } from "./store";
import { api, onNoCredits } from "./api";

// v4 (V4-SH): the share page (/s/<token>) is public and loads on its own
const ShareScreen = lazy(() => import("./screens/Share"));

const SCREEN_NAME: Record<string, string> = { models: "the Models panel (every video, image, voice, music and sound model with its price)", home: "Home (the channel's collections and videos)", channel: "Home for another of their channels", channelSettings: "the Channel page (the channel's brand, narrator, fonts, intro and outro, where it publishes)", library: "the Library", credits: "the Credits page", settings: "Settings (voices and models)", invites: "the admin Invites page", people: "the admin People page (who is waiting for access, balances, giving credits)", costs: "the admin Costs page", personSpend: "the admin page for one account's spend (what it cost us against what it was charged)", feedback: "the admin Feedback page (problems, ideas and unmet asks)", reports: "the admin Reports page (what people reported on share links)", showcase: "the admin Showcase page (the model showcase job: its status, plan, cap, Start and Stop)", safety: "the admin Safety page (blocked uploads and prompts, quiet flags such as possible minors, account locks waiting on review, and the real-person consent log)" };

// v2.1 (D): the auth gate (Landing / sign in / pending / app) and, with a Clerk key, ClerkProvider around it all
export function App() {
  return (
    <AuthRoot>
      <Studio />
    </AuthRoot>
  );
}

function Studio() {
  const { path } = useLocation();
  const auth = useStore((s) => s.auth);
  const route = match(path);
  const gate = useGate(path); // v2.1 (D)
  const ready = gate.screen === "app"; // signed in with access (a pending account isn't)

  // live progress + credit changes while signed in
  useEffect(() => {
    if (!ready) return;
    startEvents();
    return stopEvents;
  }, [ready]);

  // Wave 3 billing: a paid step short of credits, from any call anywhere, opens the credit packs inline
  useEffect(() => {
    if (!ready) return;
    return onNoCredits((e) => void openPacksForError(e));
  }, [ready]);

  // v2.1: end the call after three quiet minutes (the Director signs off first)
  useEffect(() => {
    if (auth !== "in") return;
    return startIdleWatch();
  }, [auth]);

  // keep the Director in step with what the user taps
  useEffect(() => {
    if (!ready) return;
    if (route.name === "project") director.context(`The user opened project ${route.id} on screen.`);
    else if (route.name === "review") director.context(`The user is reviewing a finished version of project ${route.id} on screen (the player, its timeline and notes).`);
    else if (SCREEN_NAME[route.name]) director.context(`The user is looking at ${SCREEN_NAME[route.name]}.`);
  }, [path, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // v4 (V4-H2): /brand is the main channel's settings now
  const channelsKnown = useStore((s) => (s.me ? s.me.channels : null));
  const { query } = useLocation();
  useEffect(() => {
    if (!ready) return;
    if (route.name === "brand" && channelsKnown) navigate(brandRedirect(channelsKnown, query.toString()), { replace: true });
  }, [path, ready, channelsKnown]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (auth === "in" && (route.name === "login" || route.name === "invite" || path === "/signup")) navigate("/", { replace: true });
  }, [auth, route.name, path]);

  // the Director changed the account (brand, onboarding...): reload the session's user, brands and balance
  const meTick = useRefresh("me");
  useEffect(() => {
    if (auth !== "in" || meTick === "0") return;
    api
      .me()
      .then((me) => set({ me, balance: me.balance }))
      .catch(() => undefined);
  }, [meTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // the legal pages (/privacy, /terms): public, before the config loads too; LEGAL_PAGES_PUBLIC=0 limits them to admins
  const legalPublic = useAuthState((s) => legalPagesPublic(s.config));
  const role = useStore((s) => s.me?.user.role ?? null);
  const legal = legalScreen(path, { isPublic: legalPublic, role });

  const shareToken = sharePath(path); // v4 (V4-SH)

  let screen;
  if (shareToken) screen = <Suspense fallback={<Splash />}><ShareScreen token={shareToken} /></Suspense>;
  else if (legal) screen = <Suspense fallback={<Splash />}><LegalScreen page={legal} /></Suspense>;
  else if (gate.screen === "splash") screen = <Splash />;
  else if (gate.screen !== "app") screen = <AuthScreen gate={gate} />; // v2.1 (D)
  else
    switch (route.name) {
      case "home":
      case "login":
      case "invite":
        screen = <HomeScreen />;
        break;
      case "project":
        screen = <ProjectScreen id={route.id} />;
        break;
      case "review":
        screen = <ReviewScreen key={route.id} id={route.id} />;
        break;
      case "library":
        screen = <LibraryScreen />;
        break;
      case "brand":
        screen = <Splash />;
        break;
      case "channel":
        screen = <HomeScreen key={route.channelId} channelId={route.channelId} />;
        break;
      case "channelSettings":
        screen = <ChannelScreen key={route.channelId} channelId={route.channelId} />;
        break;
      case "collection":
        screen = <CollectionScreen id={route.id} />;
        break;
      case "character":
        // v4 (V4-C4): /k/:id/cast/:c and /p/:id/cast/:c
        screen = <CharacterScreen key={`${route.collectionId ?? route.projectId}/${route.characterId}`} characterId={route.characterId} collectionId={route.collectionId} projectId={route.projectId} />;
        break;
      case "credits":
        screen = <CreditsScreen />;
        break;
      case "settings":
        screen = <SettingsScreen />;
        break;
      case "models":
        screen = <ModelsScreen />;
        break;
      case "invites":
        screen = <InvitesScreen />;
        break;
      case "people":
        screen = <PeopleScreen />;
        break;
      case "costs":
        screen = <AdminCostsScreen />;
        break;
      case "services":
        screen = <AdminServicesScreen />;
        break;
      case "personSpend":
        screen = <PersonSpendScreen id={route.id} />;
        break;
      case "feedback":
        screen = <AdminFeedbackScreen />;
        break;
      case "reports":
        screen = <AdminReportsScreen />;
        break;
      case "showcase":
        screen = <AdminShowcaseScreen />;
        break;
      case "safety":
        screen = <AdminSafetyScreen />;
        break;
      default:
        screen = (
          <Shell>
            <div className="page">
              <Empty title="Nothing on this reel" action={<Link to="/" className="btn btn-primary">Go home</Link>}>
                That page doesn't exist. Your videos are on Home.
              </Empty>
            </div>
          </Shell>
        );
    }

  return (
    <>
      {screen}
      {ready && <NewVideoHost />}
      {ready && <TourHost />}
      {ready && <StageLayer />}
      {ready && <UploadHost />}
      {ready && <PacksHost />}
      {ready && <ReportButton />}
      {ready && !shareToken && <ShareHost />}
      <Toasts />
      <PlayerOverlay />
    </>
  );
}

function Splash() {
  return (
    <div className="splash" role="status" aria-label="Loading CAO Studio">
      <div className="splash-logo">
        <Logo layout="stacked" height="100%" state="standby" decorative />
      </div>
      <p className="splash-tag">Direct your creative vision</p>
      <div className="splash-bar" aria-hidden="true" />
    </div>
  );
}
